import { Box, Collapse, RadioGroup, Typography } from "@mui/material";
import DotLegalCollapse from "@common/components/dotLegalCollapse/DotLegalCollapse";
import { CounterPartySubContractorViewModel, CounterPartyViewModel } from "../../../Contract.types";
import { SelectableLegalUnitsItem } from "../../../../../processingActivity/ProcessingActivity.types";
import { useTranslation } from "@localization/useTranslation.ts";
import { ValidationError } from "@common/validationError.ts";
import SharingsLegalEntity from "../../../../../processingActivity/sharingOfDataStep/sharingsLegalEntityBox/sharingsLegalEntity";
import SegmentStep from "../../../../../processingActivity/stepSegment/StepSegment";
import useCounterPartyHooks from "./CounterParty.hooks";
import { DotLegalButton, DotLegalHeader, DotLegalRadioButton, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useUserContext } from "../../../../../auth/userContextProvider/UserContextProvider";
import { CreateLegalEntityReturnType } from "../../../../../legalEntity/legalEntities/LegalEntities.types";
import { counterPartiesStepStyles } from "../CounterPartiesStep.styles";
import TextFieldWithLink from "@common/components/textFieldWithLink/TextFieldWithLink";
import DotLegalRemovableItems, { IDotLegalRemovableItem } from "@common/components/dotLegalRemovableItems/DotLegalRemovableItems";

export interface ICounterParty {
    counterParty: CounterPartyViewModel;
    isExpanded: boolean;
    onExpandClick: () => void;
    onRemoveCounterParty: () => void;
    onClearParty: () => void;
    partyOptions?: Array<SelectableLegalUnitsItem>;
    legalEntityData?: Array<SelectableLegalUnitsItem>;
    legalEntityIsLoading: boolean;
    validationErrors: Array<ValidationError>;
    onCounterPartyChange: (id: string, changeFunc: (counterParty: CounterPartyViewModel) => void) => Promise<void>;
    onSubContractorChange: (
        contractPartyId: string,
        subContractorId: string,
        changeFunc: (subContractor: CounterPartySubContractorViewModel) => void
    ) => Promise<void>;
    contractId: string;
    contractName?: string;
    selectedLegalentities: Array<string>;
    hasSubContractors: boolean;
    showAddNewEntityButton: boolean;
    partyPlaceholder: string;
    readonly?: boolean;
}

function CounterParty(props: ICounterParty) {
    const {
        stepDataUpdater,
        setShowSubContractors,
        showSubContractors,
        addSubContractor,
        isAddingSubContractor,
        removeSubContractor,
        removingSubContractorId,
    } = useCounterPartyHooks(props);
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();

    function getAvailableLegalEntities(legalEntityId?: string) {
        return props.partyOptions?.filter((x) => {
            const isCurrentEntity = x.id === legalEntityId;
            const isCounterparty = x.id === props.counterParty.legalEntityId;
            const isSubcontractor = props.counterParty.subContractors.some((s) => s.legalEntityId === x.id);
            const isLegalEntityOnOtherParty = props.selectedLegalentities.includes(x.id);

            return isCurrentEntity || (!isCounterparty && !isSubcontractor && !isLegalEntityOnOtherParty);
        });
    }

    function getAvailableSubContractors(legalEntityId?: string) {
        return props.legalEntityData?.filter((x) => {
            const isCurrentEntity = x.id === legalEntityId;
            const isCounterparty = x.id === props.counterParty.legalEntityId;
            const isSubcontractor = props.counterParty.subContractors.some((s) => s.legalEntityId === x.id);

            return isCurrentEntity || (!isCounterparty && !isSubcontractor);
        });
    }

    function getSubContractor(subContractor: CounterPartySubContractorViewModel) {
        return (
            <Box justifyContent="center" display="flex">
                <SegmentStep size="fullWidth">
                    <Box sx={counterPartiesStepStyles.partyContent}>
                        {subContractor.isLink ? (
                            <TextFieldWithLink
                                label={translateString("linkToSecondarySupplier")}
                                placeholder={translateString("enterLinkToSecondarySupplier")}
                                link={subContractor.link}
                                onLinkChange={(value) => stepDataUpdater.onSubContractorChange(subContractor.id, null, value)}
                                disabled={!permissions.contractManagementPermissions.permissions.edit || props.readonly}
                            />
                        ) : (
                            <Box>
                                <SharingsLegalEntity
                                    isLoading={props.legalEntityIsLoading}
                                    selectedItem={subContractor.legalEntityId}
                                    placeholder={translateString("whoIsSecondarySupplier")}
                                    label={translateString("secondarySupplier")}
                                    options={getAvailableSubContractors(subContractor.legalEntityId)}
                                    toolTipText={translateString("addContractPartyTooltip")}
                                    onLegalEntityChange={(legalEntityId) => stepDataUpdater.onSubContractorChange(subContractor.id, legalEntityId, undefined)}
                                    showAddNewEntityButton={!props.readonly}
                                    createAndNavigateName={props.contractName}
                                    createAndNavigateReturnType={CreateLegalEntityReturnType.Contract}
                                    disabled={!permissions.contractManagementPermissions.permissions.edit || props.readonly}
                                />
                                <DotLegalSelect
                                    selectedItem={subContractor.hasAgreement.toString()}
                                    label={translateString("agreement")}
                                    placeholder={translateString("agreement")}
                                    disableClearable
                                    onChange={(option) =>
                                        stepDataUpdater.onHasAgreementChange(subContractor.id, option ? option.toLowerCase() === "true" : false)
                                    }
                                    options={[
                                        {
                                            name: translateString("yes"),
                                            id: "true",
                                        },
                                        { name: translateString("no"), id: "false" },
                                    ]}
                                    toolTipText={translateString("contractPartyHasAgreementTooltip")}
                                    noOptionsLabel={translateString("noOptions")}
                                    disabled={!permissions.contractManagementPermissions.permissions.edit || props.readonly}
                                />
                            </Box>
                        )}
                    </Box>
                </SegmentStep>
            </Box>
        );
    }

    function getSubContractorItems(): Array<IDotLegalRemovableItem> {
        const canRemove = permissions.contractManagementPermissions.permissions.edit && !props.readonly;

        return props.counterParty.subContractors.map((subContractor) => ({
            key: subContractor.id,
            content: getSubContractor(subContractor),
            removeTooltip: translateString("removeSecondarySupplier"),
            onRemove: canRemove ? () => removeSubContractor(subContractor.id) : undefined,
            removeDisabled: removingSubContractorId === subContractor.id,
        }));
    }

    return (
        <>
            <DotLegalCollapse
                centerContent={<Typography>{props.legalEntityData?.find((x) => x.id === props.counterParty.legalEntityId)?.name}</Typography>}
                sx={{ marginBottom: 2 }}
                isExpanded={props.isExpanded}
                onExpandClick={() => props.onExpandClick()}
                onRemoveClick={() => props.onRemoveCounterParty()}
                removeItemText={translateString("removeContractParty")}
                removeItemStyle="link"
                removeItemSize="small"
                error={props.validationErrors.some((e) => e.field.startsWith(props.counterParty.id.toString()))}
                isLoading={props.legalEntityIsLoading}
                disabled={!permissions.contractManagementPermissions.permissions.edit || props.readonly}
            >
                <Box sx={{ marginBottom: 2, width: "100%" }}>
                    <SegmentStep size="fullWidth">
                        <Box sx={counterPartiesStepStyles.partyContent}>
                            <SharingsLegalEntity
                                isLoading={props.legalEntityIsLoading}
                                selectedItem={props.counterParty.legalEntityId}
                                placeholder={props.partyPlaceholder}
                                label={translateString("legalEntity")}
                                options={getAvailableLegalEntities(props.counterParty.legalEntityId)}
                                toolTipText={translateString("addContractPartyTooltip")}
                                onLegalEntityChange={stepDataUpdater.onLegalEntityChange}
                                showAddNewEntityButton={props.showAddNewEntityButton}
                                createAndNavigateName={props.contractName}
                                createAndNavigateReturnType={CreateLegalEntityReturnType.Contract}
                                disabled={!permissions.contractManagementPermissions.permissions.edit || props.readonly}
                                errorText={props.validationErrors.find((e) => e.field === `${props.counterParty.id}.legalEntityId`)?.error}
                            />

                            {props.counterParty.legalEntityId && (
                                <Box sx={{ display: "flex", alignItems: "center", marginBottom: 3, marginTop: 3, flexDirection: "column" }}>
                                    <DotLegalHeader marginBottom={2} headerStyle={"small"} toolTip={translateString("contractPartyUseSubContractorsTooltip")}>
                                        {translateString("contractPartyUseSubContractors")}
                                    </DotLegalHeader>
                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 3 }}>
                                        <DotLegalHeader headerStyle={"small"} fontWeight={100} marginBottom={0}>
                                            {translateString("secondarySuppliers")}
                                        </DotLegalHeader>
                                        <RadioGroup
                                            row
                                            value={props.counterParty.subContractors.length > 0 || showSubContractors ? 1 : 0}
                                            onChange={(e) => {
                                                const show = Number((e.target as HTMLInputElement).value) === 1 ? true : false;
                                                setShowSubContractors(show);
                                            }}
                                        >
                                            <DotLegalRadioButton
                                                disabled={
                                                    props.counterParty.subContractors.length > 0 ||
                                                    !permissions.contractManagementPermissions.permissions.edit ||
                                                    props.readonly
                                                }
                                                value={1}
                                                labelPlacement="end"
                                                label={translateString("yes")}
                                            />
                                            <DotLegalRadioButton
                                                disabled={
                                                    props.counterParty.subContractors.length > 0 ||
                                                    !permissions.contractManagementPermissions.permissions.edit ||
                                                    props.readonly
                                                }
                                                value={0}
                                                label={translateString("no")}
                                            />
                                        </RadioGroup>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </SegmentStep>

                    <Collapse in={props.counterParty.subContractors.length > 0 || showSubContractors} sx={{ width: "100%" }}>
                        <DotLegalRemovableItems items={getSubContractorItems()} />

                        {!props.readonly && (
                            <Box sx={{ marginTop: 3, display: "flex", justifyContent: "center", gap: 1 }}>
                                <DotLegalButton
                                    buttonType="secondary"
                                    onClick={() => {
                                        addSubContractor(false);
                                    }}
                                    isLoading={isAddingSubContractor}
                                    disabled={!permissions.contractManagementPermissions.permissions.edit}
                                    size={"small"}
                                >
                                    {props.counterParty.subContractors?.length > 0
                                        ? translateString("addAnotherSecondarySupplier")
                                        : translateString("addSecondarySupplier")}
                                </DotLegalButton>

                                <DotLegalButton
                                    buttonType="secondary"
                                    onClick={() => addSubContractor(true)}
                                    disabled={!permissions.contractManagementPermissions.permissions.edit}
                                    size={"small"}
                                >
                                    {translateString("insertLinkToSystemSecondarySupplier")}
                                </DotLegalButton>
                            </Box>
                        )}
                    </Collapse>
                </Box>
            </DotLegalCollapse>
        </>
    );
}

export default CounterParty;
